import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Calendar, CheckCircle2, Clock, Euro, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SITE } from "@/data/content";
import ScheduleManager from "@/pages/ScheduleManager";
import ServicesManager from "@/pages/ServicesManager";
import ContentManager from "@/pages/ContentManager";
import ReviewsManager from "@/pages/ReviewsManager";

const weekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const months = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
const prettyDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${weekdays[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
};
const fmtPrice = (p) => (p === 0 ? "Gratuita" : `${p}€`);

const statusMap = {
  confirmed: { label: "Confirmada", cls: "bg-green-50 text-green-700 border-green-200" },
  pending_payment: { label: "Pagamento pendente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cancelled: { label: "Cancelada", cls: "bg-stone-100 text-stone-500 border-stone-200" },
  completed: { label: "Realizada", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const filters = [
  { id: "all", label: "Todas" },
  { id: "confirmed", label: "Confirmadas" },
  { id: "pending_payment", label: "Pendentes" },
  { id: "completed", label: "Realizadas" },
  { id: "cancelled", label: "Canceladas" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");

  const load = useCallback(async () => {
    setLoading(true);
    const [s, b] = await Promise.all([
      api.get("/admin/stats"),
      api.get(`/admin/bookings?status=${filter}`),
    ]);
    setStats(s.data);
    setBookings(b.data);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Marcação atualizada.");
      load();
    } catch { toast.error("Não foi possível atualizar."); }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FAF7F7]">
      <header className="bg-white border-b border-rose-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <p className="font-display text-2xl text-stone-900">{SITE.name}<span className="text-rose-700">.</span> <span className="text-sm text-stone-400 font-sans">painel</span></p>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-500 hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-rose-800">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center gap-6 border-b border-rose-100 mb-8 overflow-x-auto">
          <button onClick={() => setTab("bookings")} data-testid="tab-bookings"
            className={`pb-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === "bookings" ? "border-rose-800 text-rose-800" : "border-transparent text-stone-400 hover:text-stone-600"}`}>
            Marcações
          </button>
          <button onClick={() => setTab("schedule")} data-testid="tab-schedule"
            className={`pb-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === "schedule" ? "border-rose-800 text-rose-800" : "border-transparent text-stone-400 hover:text-stone-600"}`}>
            Horários
          </button>
          <button onClick={() => setTab("services")} data-testid="tab-services"
            className={`pb-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === "services" ? "border-rose-800 text-rose-800" : "border-transparent text-stone-400 hover:text-stone-600"}`}>
            Serviços
          </button>
          <button onClick={() => setTab("content")} data-testid="tab-content"
            className={`pb-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === "content" ? "border-rose-800 text-rose-800" : "border-transparent text-stone-400 hover:text-stone-600"}`}>
            Conteúdo
          </button>
          <button onClick={() => setTab("reviews")} data-testid="tab-reviews"
            className={`pb-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === "reviews" ? "border-rose-800 text-rose-800" : "border-transparent text-stone-400 hover:text-stone-600"}`}>
            Testemunhos
          </button>
        </div>

        {tab === "schedule" ? (
          <ScheduleManager />
        ) : tab === "services" ? (
          <ServicesManager />
        ) : tab === "content" ? (
          <ContentManager />
        ) : tab === "reviews" ? (
          <ReviewsManager />
        ) : (
          <>
            <h1 className="font-display text-4xl text-stone-900">Marcações</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="admin-stats">
          <Stat icon={Calendar} label="Total" value={stats?.total ?? "—"} />
          <Stat icon={CheckCircle2} label="Confirmadas" value={stats?.confirmed ?? "—"} />
          <Stat icon={Clock} label="Próximas" value={stats?.upcoming ?? "—"} />
          <Stat icon={Euro} label="Receita" value={stats ? `${stats.revenue}€` : "—"} />
        </div>

        <div className="flex flex-wrap gap-2 mt-10">
          {filters.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`admin-filter-${f.id}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                filter === f.id ? "bg-rose-800 text-white border-rose-800" : "bg-white border-rose-100 text-stone-600 hover:border-rose-300"
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-rose-100 overflow-hidden" data-testid="admin-bookings">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-700" /></div>
          ) : bookings.length === 0 ? (
            <p className="p-12 text-center text-stone-400">Sem marcações nesta categoria.</p>
          ) : (
            <div className="divide-y divide-rose-50">
              {bookings.map((b) => {
                const st = statusMap[b.status] || statusMap.confirmed;
                return (
                  <div key={b.id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`booking-row-${b.id}`}>
                    <div className="lg:w-40 shrink-0">
                      <p className="font-medium text-stone-900">{prettyDate(b.date)}</p>
                      <p className="text-sm text-stone-500">{b.time}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-stone-900">{b.name} <span className="text-stone-400">· {b.service_name}</span></p>
                      <p className="text-sm text-stone-500 truncate">{b.email} · {b.phone}</p>
                      {b.notes && <p className="text-sm text-stone-400 mt-1 italic truncate">“{b.notes}”</p>}
                    </div>
                    <div className="lg:w-24 text-stone-900 font-display text-xl">{fmtPrice(b.price)}</div>
                    <span className={`text-xs px-3 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                    <div className="flex gap-2">
                      {b.status !== "confirmed" && b.status !== "cancelled" && (
                        <button onClick={() => update(b.id, "confirmed")} data-testid={`confirm-${b.id}`}
                          className="text-xs rounded-full border border-green-200 text-green-700 px-3 py-1.5 hover:bg-green-50">Confirmar</button>
                      )}
                      {b.status === "confirmed" && (
                        <button onClick={() => update(b.id, "completed")} data-testid={`complete-${b.id}`}
                          className="text-xs rounded-full border border-rose-200 text-rose-700 px-3 py-1.5 hover:bg-rose-50">Marcar realizada</button>
                      )}
                      {b.status !== "cancelled" && (
                        <button onClick={() => update(b.id, "cancelled")} data-testid={`cancel-${b.id}`}
                          className="text-xs rounded-full border border-stone-200 text-stone-500 px-3 py-1.5 hover:bg-stone-50">Cancelar</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
          </>
        )}
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-rose-100 p-5">
    <div className="flex items-center gap-2 text-stone-400 text-xs uppercase tracking-wide">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <p className="font-display text-4xl text-stone-900 mt-3">{value}</p>
  </div>
);
